import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation, Trans } from 'react-i18next';
import {
    Card,
    CardHeader,
    CardContent,
    Avatar,
    FormControlLabel,
    Checkbox,
    TextField,
    Tooltip,
} from '@mui/material';
import AutoComplete from '@mui/material/Autocomplete';
import InfoIcon from '@mui/icons-material/Info';
import DurationInput from '../Step2-BreakRuleSection/DurationInput';
import { UPDATE_VALIDATION_RULE, ADD_ROLLING_PERIOD_RULE, REMOVE_ROLLING_PERIOD_RULE_AT_INDEX } from '../../../store/ruleSet';
import InputTitle from '../InputTitle';
import StyledSwitch from '../StyledSwitch';
import MaxHoursPerRollingPeriod from './MaxHoursPerRollingPeriod';
import MaxHoursPerRollingPeriodRulesSummary from './MaxHoursPerRollingPeriodRulesSummary';
import ActualHours from './ActualHours';
import { RuleSetType, MaxConsecutiveDayWeekType } from '../../../../Enums';
import './Step3-ScheduleRulesSection.scss';
import OverrideDuration from './OverrideDuration';

// Each override cannot sensibly exceed the period it caps.
const MAX_HOURS_PER_DAY = 24;
const MAX_HOURS_PER_WEEK = 7 * 24;
const MAX_BI_WEEKLY_HOURS = 14 * 24;

// Mirrors ConsecutiveHoursRuleHelper.CONSECUTIVE_MINUTES_MAXIMUM, the widest chain the rule can measure.
const MAX_CONSECUTIVE_HOURS = 72;

/**
 * @param {{ isDisabled: boolean, validationErrors: {[Key: string], isDisabled: boolean}, stepNumber: number  }}
 */
export default function ScheduleRulesSection(props) {
    const { isDisabled, validationErrors, stepNumber } = props;
    const { t } = useTranslation('employmentBillboard', { keyPrefix: 'settings.ruleSet.scheduleRulesStep' });
    const dispatch = useDispatch();
    /** @type {RuleSet.IRuleSetDetails} */
    const adminRuleSet = useSelector((/** @type {EmploymentBillboard.AdminStore} */ state) => state.adminRuleSet);
    /** @type {AdminAppData.IAppData} */
    const adminAppData = useSelector((/** @type {EmploymentBillboard.AdminStore} */ state) => state.adminAppData);
    const {
        isMaxHoursPerDay, isIncludeBreaksInMaxHoursPerDay, isUseActualHoursInMaxHoursPerDay, maxHoursPerDayBreakRule,
        isMaxHoursPerWeek, isIncludeBreaksInMaxHoursPerWeek, isUseActualHoursInMaxHoursPerWeek, maxHoursPerWeekBreakRule,
        isRestTimeBetweenShift, isUseActualHoursInRestTimeBetweenShift,
        isMaxHoursPerRollingPeriod, maxHoursInRollingPeriodRules, isUseActualHoursInMaxHoursInRollingPeriod, isIncludeBreaksInMaxHoursInRollingPeriod, maxHoursInRollingPeriodBreakRule,
        isMaxShiftsPerDay, isMaxConsecutiveDay, maxConsecutiveDayWeekType, isOverrideMaxHoursPerWeek, isOverrideMaxHoursPerDay, overrideMaxHoursPerWeekInMinutes, overrideMaxHoursPerDayInMinutes,
        isMaxBiWeeklyHours, isIncludeBreaksInMaxBiWeeklyHours, isOverrideBiWeeklyDefaultValue, overrideBiWeeklyDefaultValueInMinutes,
        isOverrideBiWeeklyAnchorDate, overrideBiWeeklyAnchorDate, isUseBiWeeklyActualHoursWorked, maxBiWeeklyHoursBreakRule,
        isConsecutiveHours, isMinConsecutiveHours, minConsecutiveHoursInMinutes, isMaxConsecutiveHours, maxConsecutiveHoursInMinutes,
    } = adminRuleSet.validationRuleSet;
    const breakRules = adminAppData.ruleSets.filter(x => x.ruleSetType === RuleSetType.BreakRule);

    const maxConsecutiveDayWeekTypeOptions = [
        { value: MaxConsecutiveDayWeekType.RollingWeek, label: t('maxConsecutiveDay.rollingWeek') },
        { value: MaxConsecutiveDayWeekType.FixedWeek, label: t('maxConsecutiveDay.fixedWeek') },
    ];

    /**
     * @param {string} target
     * @param {string} value
     */
    const updateValidationRulesStore = (target, value) => {
        let otherTargets = {};
        if (target === 'isMaxHoursPerRollingPeriod') {
            otherTargets = {
                maxHoursInRollingPeriodRules: [],
                isUseActualHoursInMaxHoursInRollingPeriod: false,
                isIncludeBreaksInMaxHoursInRollingPeriod: false,
                maxHoursInRollingPeriodBreakRule: null,
            }
        }
        else if(target === 'isMaxHoursPerDay'){
            otherTargets = {
                isIncludeBreaksInMaxHoursPerDay: false,
                isUseActualHoursInMaxHoursPerDay: false,
                maxHoursPerDayBreakRule: null,
                isOverrideMaxHoursPerDay: false,
                overrideMaxHoursPerDayInMinutes: null,
            }
        }
        else if(target === 'isMaxHoursPerWeek'){
            otherTargets = {
                isIncludeBreaksInMaxHoursPerWeek: false,
                isUseActualHoursInMaxHoursPerWeek: false,
                maxHoursPerWeekBreakRule: null,
                isOverrideMaxHoursPerWeek: false,
                overrideMaxHoursPerWeekInMinutes: null,
            }
        }
        else if(target === 'isRestTimeBetweenShift'){
            otherTargets = {
                isUseActualHoursInRestTimeBetweenShift: false,
            }
        }
        else if(target === 'isOverrideMaxHoursPerDay'){
            otherTargets = {
                isOverrideMaxHoursPerDay: value,
                overrideMaxHoursPerDayInMinutes: null,
            }
        }
        else if(target === 'isOverrideMaxHoursPerWeek'){
            otherTargets = {
                isOverrideMaxHoursPerWeek: value,
                overrideMaxHoursPerWeekInMinutes: null,
            }
        }
        else if(target === 'overrideMaxHoursPerDayInMinutes'){
            otherTargets = {
                overrideMaxHoursPerDayInMinutes: value,
            }
        }
        else if(target === 'overrideMaxHoursPerWeekInMinutes'){
            otherTargets = {
                overrideMaxHoursPerWeekInMinutes: value,
            }
        }
        else if (target === 'isMaxBiWeeklyHours') {
            otherTargets = {
                isIncludeBreaksInMaxBiWeeklyHours: false,
                isOverrideBiWeeklyDefaultValue: false,
                overrideBiWeeklyDefaultValueInMinutes: null,
                isOverrideBiWeeklyAnchorDate: false,
                overrideBiWeeklyAnchorDate: null,
                isUseBiWeeklyActualHoursWorked: false,
            };
        } else if (target === 'isOverrideBiWeeklyDefaultValue') {
            otherTargets = {
                isOverrideBiWeeklyDefaultValue: value,
                overrideBiWeeklyDefaultValueInMinutes: null,
            };
        } else if (target === 'isOverrideBiWeeklyAnchorDate') {
            otherTargets = {
                isOverrideBiWeeklyAnchorDate: value,
                overrideBiWeeklyAnchorDate: null,
            };
        } else if (target === 'isConsecutiveHours') {
            otherTargets = {
                isMinConsecutiveHours: false,
                minConsecutiveHoursInMinutes: null,
                isMaxConsecutiveHours: false,
                maxConsecutiveHoursInMinutes: null,
            };
        } else if (target === 'isMinConsecutiveHours') {
            otherTargets = {
                isMinConsecutiveHours: value,
                minConsecutiveHoursInMinutes: null,
            };
        } else if (target === 'isMaxConsecutiveHours') {
            otherTargets = {
                isMaxConsecutiveHours: value,
                maxConsecutiveHoursInMinutes: null,
            };
        }
        dispatch({
            type: UPDATE_VALIDATION_RULE,
            payload: {
                [target]: value,
                ...otherTargets,
            },
        });
    };

    /**
     * 
     * @param {number} maxHoursInRollingPeriod 
     * @param {number} rollingPeriodNumber 
     * @param {RollingPeriodUnit} rollingPeriodUnit 
     */
    const onAddRollingRule = (maxHoursInRollingPeriod, rollingPeriodNumber, rollingPeriodUnit) => {
        dispatch({
            type: ADD_ROLLING_PERIOD_RULE,
            payload: {
                maxHoursInRollingPeriod, rollingPeriodNumber, rollingPeriodUnit,
            },
        });
    }

    /**
     * @param {number} index
     */
     const onDeleteRollingRule = (index) => {
        dispatch({
            type: REMOVE_ROLLING_PERIOD_RULE_AT_INDEX,
            payload: index,
        });
    };

    return (
        <Card className="card">
            <CardHeader
                className="card--header"
                titleTypographyProps={{ gutterBottom: true }}
                avatar={<Avatar className="avatar">{stepNumber}</Avatar>}
                title="Schedule rules"
            />
            <CardContent className="no-gap">
                <InputTitle>{t('header')}</InputTitle>
                <div className={`${isMaxHoursPerDay ? 'open-sub-section' : ''}`}>
                    <StyledSwitch
                        checked={isMaxHoursPerDay}
                        onChange={(value) => updateValidationRulesStore('isMaxHoursPerDay', value)}
                        label={t('maxHoursPerDay.label')}
                        tooltip={t('maxHoursPerDay.tooltip')}
                        disabled={isDisabled}
                    />
                    {isMaxHoursPerDay && (
                        <div>
                            <FormControlLabel
                                control={(
                                    <Checkbox
                                        checked={isIncludeBreaksInMaxHoursPerDay}
                                        onClick={({ target: { checked: isChecked } }) => updateValidationRulesStore('isIncludeBreaksInMaxHoursPerDay', isChecked)}
                                    />
                                )}
                                label={t('breakRules.label')}
                                disabled={isDisabled}
                            />
                            <OverrideDuration 
                                label={t('overrideDuration.label')} 
                                placeholder={t('overrideDuration.placeholder')}
                                isOverride={isOverrideMaxHoursPerDay}
                                overrideValue={overrideMaxHoursPerDayInMinutes}
                                setIsOverride={(checked) => updateValidationRulesStore('isOverrideMaxHoursPerDay', checked)}
                                setOverrideValue={(value) => updateValidationRulesStore('overrideMaxHoursPerDayInMinutes', value)}
                                helpIconTooltip={t('overrideDuration.toolTip')}
                                isDisabled={isDisabled}
                                maxHours={MAX_HOURS_PER_DAY}
                                />
                            {
                                validationErrors.overrideMaxHoursPerDayInMinutes && isOverrideMaxHoursPerDay &&  overrideMaxHoursPerDayInMinutes <= 0 ? <span style={{ color: 'red' }}>{t('overrideDuration.error')}</span> : null
                            }
                            <ActualHours
                                isActualHours={isUseActualHoursInMaxHoursPerDay}
                                setIsActualHours={(checked) => updateValidationRulesStore('isUseActualHoursInMaxHoursPerDay', checked)}
                                breakRuleId={maxHoursPerDayBreakRule}
                                setBreakRuleId={(id) => updateValidationRulesStore('maxHoursPerDayBreakRule', id)}
                                breakRules={breakRules}
                                helpIconTooltip={t('maxHoursPerDay.isUseActualHoursInMaxHoursPerDayTooltip')}
                                isDisabled={isDisabled}
                            />
                        </div>
                    )}
                </div>
                <div className={`${isMaxHoursPerWeek ? 'open-sub-section' : ''}`}>
                    <StyledSwitch
                        checked={isMaxHoursPerWeek}
                        onChange={(value) => updateValidationRulesStore('isMaxHoursPerWeek', value)}
                        label={t('maxHoursPerWeek.label')}
                        tooltip={t('maxHoursPerWeek.tooltip')}
                        disabled={isDisabled}
                    />
                    {isMaxHoursPerWeek && (
                        <div>
                            <FormControlLabel
                                control={(
                                    <Checkbox
                                        checked={isIncludeBreaksInMaxHoursPerWeek}
                                        onClick={({ target: { checked: isChecked } }) => updateValidationRulesStore('isIncludeBreaksInMaxHoursPerWeek', isChecked)}
                                    />
                                )}
                                label={t('breakRules.label')}
                                disabled={isDisabled}
                            />
                            <OverrideDuration 
                                label={t('overrideDuration.label')} 
                                placeholder={t('overrideDuration.placeholder')}
                                isOverride={isOverrideMaxHoursPerWeek}
                                overrideValue={overrideMaxHoursPerWeekInMinutes}
                                setIsOverride={(checked) => updateValidationRulesStore('isOverrideMaxHoursPerWeek', checked)}
                                setOverrideValue={(value) => updateValidationRulesStore('overrideMaxHoursPerWeekInMinutes', value)}
                                helpIconTooltip={t('overrideDuration.toolTip')}
                                isDisabled={isDisabled}
                                maxHours={MAX_HOURS_PER_WEEK}
                                />
                            {
                                validationErrors.overrideMaxHoursPerWeekInMinutes && isOverrideMaxHoursPerWeek &&  overrideMaxHoursPerWeekInMinutes <= 0 ? <span style={{ color: 'red' }}>{t('overrideDuration.error')}</span> : null
                            }                                
                            <ActualHours
                                isActualHours={isUseActualHoursInMaxHoursPerWeek}
                                setIsActualHours={(checked) => updateValidationRulesStore('isUseActualHoursInMaxHoursPerWeek', checked)}
                                breakRuleId={maxHoursPerWeekBreakRule}
                                setBreakRuleId={(id) => updateValidationRulesStore('maxHoursPerWeekBreakRule', id)}
                                breakRules={breakRules}
                                helpIconTooltip={t('maxHoursPerWeek.isUseActualHoursInMaxHoursPerWeekTooltip')}
                                isDisabled={isDisabled}
                            />
                        </div>
                )}
                </div>

                <div className={`${isRestTimeBetweenShift ? 'open-sub-section' : ''}`}>
                    <StyledSwitch
                        checked={isRestTimeBetweenShift}
                        onChange={(value) => updateValidationRulesStore('isRestTimeBetweenShift', value)}
                        label={t('restTimeBetweenShift.label')}
                        tooltip={t('restTimeBetweenShift.tooltip')}
                        disabled={isDisabled}
                    />
                    {isRestTimeBetweenShift && (
                        <ActualHours
                            isActualHours={isUseActualHoursInRestTimeBetweenShift}
                            setIsActualHours={(checked) => updateValidationRulesStore('isUseActualHoursInRestTimeBetweenShift', checked)}
                            isSelectBreakRuleId={false}
                            helpIconTooltip={t('restTimeBetweenShift.isUseActualHoursInRestTimeBetweenShiftTooltip')}
                            isDisabled={isDisabled}
                        />
                    )}
                </div>
                <StyledSwitch
                    checked={isMaxShiftsPerDay}
                    onChange={(value) => updateValidationRulesStore('isMaxShiftsPerDay', value)}
                    label={t('maxShiftsPerDay.label')}
                    tooltip={t('maxShiftsPerDay.tooltip')}
                    disabled={isDisabled}
                />
                
                <div className={`${isMaxHoursPerRollingPeriod ? 'open-sub-section' : ''}`}>
                    <StyledSwitch
                        checked={isMaxHoursPerRollingPeriod}
                        onChange={(value) => updateValidationRulesStore('isMaxHoursPerRollingPeriod', value)}
                        label={t('rollingPeriodRules.label')}
                        tooltip={t('rollingPeriodRules.tooltip')}
                        disabled={isDisabled}
                    />
                    {
                        isMaxHoursPerRollingPeriod && (
                            <div>
                                <FormControlLabel
                                    control={(
                                        <Checkbox
                                            checked={isIncludeBreaksInMaxHoursInRollingPeriod}
                                            onClick={({ target: { checked: isChecked } }) => updateValidationRulesStore('isIncludeBreaksInMaxHoursInRollingPeriod', isChecked)}
                                        />
                                    )}
                                    label={t('breakRules.label')}
                                    disabled={isDisabled}
                                />
                                <ActualHours
                                    isActualHours={isUseActualHoursInMaxHoursInRollingPeriod}
                                    setIsActualHours={(checked) => updateValidationRulesStore('isUseActualHoursInMaxHoursInRollingPeriod', checked)}
                                    breakRuleId={maxHoursInRollingPeriodBreakRule}
                                    setBreakRuleId={(id) => updateValidationRulesStore('maxHoursInRollingPeriodBreakRule', id)}
                                    breakRules={breakRules}
                                    helpIconTooltip={t('rollingPeriodRules.isIncludeBreaksInMaxHoursInRollingPeriodTooltip')}
                                    isDisabled={isDisabled}
                                />
                                <MaxHoursPerRollingPeriod
                                    onAdd={onAddRollingRule}
                                    isDisabled={isDisabled || maxHoursInRollingPeriodRules.length >= 2}
                                />
                                {
                                    maxHoursInRollingPeriodRules && !!maxHoursInRollingPeriodRules.length && (
                                        <MaxHoursPerRollingPeriodRulesSummary
                                            onDelete={onDeleteRollingRule}
                                            maxHoursInRollingPeriodRules={maxHoursInRollingPeriodRules}
                                            isDisabled={isDisabled}
                                        />
                                    )
                                }
                                {
                                    validationErrors.isMaxHoursPerRollingPeriod ? <span style={{ color: 'red' }}>You must select at least one rolling period rule</span> : null
                                }
                            </div>
                        )
                    }
                </div>
                <div className={`${isMaxConsecutiveDay ? 'open-sub-section' : ''}`}>
                    <StyledSwitch
                        checked={isMaxConsecutiveDay}
                        onChange={(value) => updateValidationRulesStore('isMaxConsecutiveDay', value)}
                        label={t('maxConsecutiveDay.label')}
                        tooltip={t('maxConsecutiveDay.tooltip')}
                        disabled={isDisabled}
                    />
                    {isMaxConsecutiveDay && (
                        <div>
                            <InputTitle>{t('maxConsecutiveDay.selectValidationPeriodOption')}</InputTitle>
                            <AutoComplete
                                id="max-consecutive-day-week-type-select"
                                value={maxConsecutiveDayWeekTypeOptions.find(x => x.value === (maxConsecutiveDayWeekType ?? MaxConsecutiveDayWeekType.RollingWeek))}
                                options={maxConsecutiveDayWeekTypeOptions}
                                onChange={(_event, optionSelected) => updateValidationRulesStore('maxConsecutiveDayWeekType', optionSelected?.value)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        placeholder={t('maxConsecutiveDay.placeholder')}
                                    />
                                )}
                                getOptionLabel={(option) => option.label}
                                includeInputInList
                                disableClearable
                                disabled={isDisabled}
                            />
                        </div>
                    )}
                </div>
                <div className={`${isMaxBiWeeklyHours ? 'open-sub-section' : ''}`}>
                    <StyledSwitch
                        checked={isMaxBiWeeklyHours}
                        onChange={(value) => updateValidationRulesStore('isMaxBiWeeklyHours', value)}
                        label={t('maxBiWeeklyHours.label')}
                        tooltip={t('maxBiWeeklyHours.tooltip')}
                        disabled={isDisabled}
                    />
                    {isMaxBiWeeklyHours && (
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <FormControlLabel
                                    control={(
                                        <Checkbox
                                            checked={isIncludeBreaksInMaxBiWeeklyHours}
                                            onClick={({ target: { checked: isChecked } }) => updateValidationRulesStore('isIncludeBreaksInMaxBiWeeklyHours', isChecked)}
                                        />
                                    )}
                                    label={t('maxBiWeeklyHours.includeBreaksLabel')}
                                    disabled={isDisabled}
                                />
                                <Tooltip title={
                                    <Trans
                                        i18nKey="maxBiWeeklyHours.includeBreaksTooltip"
                                        t={t}
                                        components={{
                                            b: <b />,
                                            br: <br />,
                                            ul: <ul style={{ margin: 0, paddingLeft: '1rem', marginTop: '8px' }} />,
                                            li: <li />
                                        }}
                                    />
                                } arrow>
                                    <InfoIcon style={{ width: '15px', height: '15px', fontSize: '15px', marginLeft: '8px', cursor: 'pointer' }} />
                                </Tooltip>
                            </div>
                            <OverrideDuration
                                label={t('maxBiWeeklyHours.overrideDefaultValueLabel')}
                                placeholder={t('overrideDuration.placeholder')}
                                isOverride={isOverrideBiWeeklyDefaultValue}
                                overrideValue={overrideBiWeeklyDefaultValueInMinutes}
                                setIsOverride={(checked) => updateValidationRulesStore('isOverrideBiWeeklyDefaultValue', checked)}
                                setOverrideValue={(value) => updateValidationRulesStore('overrideBiWeeklyDefaultValueInMinutes', value)}
                                helpIconTooltip={t('overrideDuration.toolTip')}
                                isDisabled={isDisabled}
                                maxHours={MAX_BI_WEEKLY_HOURS}
                            />
                            {
                                validationErrors.overrideBiWeeklyDefaultValueInMinutes && isOverrideBiWeeklyDefaultValue && overrideBiWeeklyDefaultValueInMinutes <= 0 ? <span style={{ color: 'red' }}>{t('overrideDuration.error')}</span> : null
                            }
                            <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
                                <FormControlLabel
                                    control={(
                                        <Checkbox
                                            checked={isOverrideBiWeeklyAnchorDate}
                                            onClick={({ target: { checked: isChecked } }) => updateValidationRulesStore('isOverrideBiWeeklyAnchorDate', isChecked)}
                                        />
                                    )}
                                    label={t('maxBiWeeklyHours.overrideAnchorDateLabel')}
                                    disabled={isDisabled}
                                />
                                <TextField
                                    type="date"
                                    value={overrideBiWeeklyAnchorDate ? overrideBiWeeklyAnchorDate.split('T')[0] : ''}
                                    onChange={(e) => updateValidationRulesStore('overrideBiWeeklyAnchorDate', e.target.value)}
                                    disabled={isDisabled || !isOverrideBiWeeklyAnchorDate}
                                    variant="outlined"
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    style={{ marginLeft: '16px' }}
                                />
                                <Tooltip title={t('maxBiWeeklyHours.overrideAnchorDateTooltip')} arrow>
                                    <InfoIcon style={{ width: '15px', height: '15px', fontSize: '15px', marginLeft: '8px', cursor: 'pointer' }} />
                                </Tooltip>
                            </div>
                            <ActualHours
                                isActualHours={isUseBiWeeklyActualHoursWorked}
                                setIsActualHours={(isChecked) => updateValidationRulesStore('isUseBiWeeklyActualHoursWorked', isChecked)}
                                breakRuleId={maxBiWeeklyHoursBreakRule}
                                setBreakRuleId={(id) => updateValidationRulesStore('maxBiWeeklyHoursBreakRule', id)}
                                breakRules={breakRules}
                                helpIconTooltip={t('maxBiWeeklyHours.useActualHoursTooltip')}
                                isDisabled={isDisabled}
                            />
                        </div>
                    )}
                </div>
                <div className={`consecutive-hours-rules-section ${isConsecutiveHours ? 'open-sub-section' : ''}`}>
                    <StyledSwitch
                        checked={isConsecutiveHours}
                        onChange={(value) => updateValidationRulesStore('isConsecutiveHours', value)}
                        label={t('consecutiveHours.label')}
                        tooltip={t('consecutiveHours.tooltip')}
                        disabled={isDisabled}
                    />
                    {isConsecutiveHours && (
                        <div>
                            {/* One grid for both bounds: the label track sizes to the wider label, so the two
                                pickers line up with no slack whatever the active language. */}
                            <div className="consecutive-hours-bounds">
                                <FormControlLabel
                                    control={(
                                        <Checkbox
                                            checked={isMinConsecutiveHours}
                                            onClick={({ target: { checked: isChecked } }) => updateValidationRulesStore('isMinConsecutiveHours', isChecked)}
                                        />
                                    )}
                                    label={t('consecutiveHours.minimumHours')}
                                    disabled={isDisabled}
                                />
                                <DurationInput
                                    onChange={(value) => updateValidationRulesStore('minConsecutiveHoursInMinutes', value * 60)}
                                    value={minConsecutiveHoursInMinutes / 60}
                                    maxHours={MAX_CONSECUTIVE_HOURS}
                                    variant="outlined"
                                    InputLabelProps={{ shrink: true }}
                                    disabled={isDisabled || !isMinConsecutiveHours}
                                    placeholder={t('overrideDuration.placeholder')}
                                />
                                <Tooltip title={t('consecutiveHours.minimumHoursTooltip')} arrow>
                                    <InfoIcon style={{ width: '15px', height: '15px', fontSize: '15px' }} />
                                </Tooltip>
                                {
                                    validationErrors.minConsecutiveHoursInMinutes
                                        ? <span style={{ color: 'red' }}>{t('consecutiveHours.durationError')}</span>
                                        : null
                                }
                                <FormControlLabel
                                    control={(
                                        <Checkbox
                                            checked={isMaxConsecutiveHours}
                                            onClick={({ target: { checked: isChecked } }) => updateValidationRulesStore('isMaxConsecutiveHours', isChecked)}
                                        />
                                    )}
                                    label={t('consecutiveHours.maximumHours')}
                                    disabled={isDisabled}
                                />
                                <DurationInput
                                    onChange={(value) => updateValidationRulesStore('maxConsecutiveHoursInMinutes', value * 60)}
                                    value={maxConsecutiveHoursInMinutes / 60}
                                    maxHours={MAX_CONSECUTIVE_HOURS}
                                    variant="outlined"
                                    InputLabelProps={{ shrink: true }}
                                    disabled={isDisabled || !isMaxConsecutiveHours}
                                    placeholder={t('overrideDuration.placeholder')}
                                />
                                <Tooltip title={t('consecutiveHours.maximumHoursTooltip')} arrow>
                                    <InfoIcon style={{ width: '15px', height: '15px', fontSize: '15px' }} />
                                </Tooltip>
                                {
                                    validationErrors.maxConsecutiveHoursInMinutes
                                        ? <span style={{ color: 'red' }}>{t('consecutiveHours.durationError')}</span>
                                        : null
                                }
                            </div>
                            {
                                validationErrors.consecutiveHoursNoBound
                                    ? <span style={{ color: 'red', display: 'block' }}>{t('consecutiveHours.noBoundError')}</span>
                                    : null
                            }
                            {
                                validationErrors.consecutiveHoursRange
                                    ? <span style={{ color: 'red', display: 'block' }}>{t('consecutiveHours.rangeError')}</span>
                                    : null
                            }
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>

    );
}
